import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import { initAuth, msalInstance, loginRequest, runtimeBranding } from '@/auth/msal-config';
import { AuthErrorScreen } from '@/auth/auth-error-screen';
import {
  LOGGED_OUT_PATH,
  shouldLeaveLoggedOutScreen,
  shouldRedirectToLogin,
  takeLoggedOutFlag,
} from '@/auth/login-gate';
import { AdminRoleProvider } from '@/auth/admin-role-provider';
import { applyBranding } from '@/branding/branding';
import { watchSystemTheme } from '@/lib/theme';
import { App } from './App';
import './index.css';

// Keep `system` theme in sync with OS changes (initial theme is applied inline
// in index.html before paint to avoid a flash).
watchSystemTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

async function bootstrap() {
  const container = document.getElementById('root')!;

  // Resolve /config.json (deployed) or the VITE_* fallback (local dev), then bring up
  // MSAL — see EW-166. A missing/invalid auth config throws (EW-227: no dev bypass), and
  // the error screen shows it instead of rendering an unauthenticated shell.
  let authError: string | null = null;
  try {
    // Branded before the sign-in round trips, so the product title never flashes.
    try {
      await initAuth();
    } finally {
      applyBranding(runtimeBranding);
    }
    await msalInstance.initialize();
    await msalInstance.handleRedirectPromise();
  } catch (err) {
    authError = err instanceof Error ? err.message : String(err);
  }

  if (authError) {
    createRoot(container).render(<AuthErrorScreen message={authError} />);
    return;
  }

  const justLoggedOut = takeLoggedOutFlag();
  const hasAccount = msalInstance.getAllAccounts().length > 0;
  if (
    shouldRedirectToLogin({
      hasAccount,
      pathname: window.location.pathname,
      justLoggedOut,
    })
  ) {
    // Nothing is rendered on this path (EW-190): the data-less admin shell must not
    // appear at all. MSAL returns the browser to this URL after the login, so the deep
    // link survives (navigateToLoginRequestUrl defaults to true).
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (err) {
      // Typically interaction_in_progress (back button from an interrupted IdP round);
      // without this catch the page would stay blank. The screen's reload restarts
      // the bootstrap, which is the way out of that state.
      const message = err instanceof Error ? err.message : String(err);
      createRoot(container).render(<AuthErrorScreen message={message} />);
    }
    return;
  }
  // The IdP sends the browser back to the origin after a logout; put it on the
  // logged-out screen before the router reads the location.
  if (justLoggedOut) {
    window.history.replaceState(null, '', LOGGED_OUT_PATH);
  } else if (shouldLeaveLoggedOutScreen({ hasAccount, pathname: window.location.pathname })) {
    window.history.replaceState(null, '', '/');
  }

  const tree = (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <MsalProvider instance={msalInstance}>
          <AdminRoleProvider>
            <App />
          </AdminRoleProvider>
        </MsalProvider>
      </QueryClientProvider>
    </StrictMode>
  );
  createRoot(container).render(tree);
}

void bootstrap();
